import { atomFamily } from "jotai-family";
import { atomWithDefault } from "jotai/utils";
import { isEqual } from "lodash-es";
import { loadable } from "../../../utils/jotai-loadable";
import { debugLabel } from "../../generate/store";
import { previewFamily } from "./preview-family";

import type { FontSelectionSummary } from "../../brand-kit/hooks/use-font-selections";
import type { BrandTone } from "../../brand-kit/server/brand-tone-service";
import type { AccountContext } from "../../generic/hooks/use-account-context";
import type { ImageUsage } from "./previews-family";
import type { MultiformatImage } from "./types";

const previewUrlFamily = atomFamily(
  ({
    formatId,
    answer,
    imgNum,
    useSelectedLogo,
    templateIndex,
    templateCode,
    dimensions,
    images,
    multipage,
    fontSelections,
    imageUsage,
    customTemplate,
    db,
    fileId,
    brandTones,
    tone,
    context,
  }: {
    formatId: string;
    answer: string;
    imgNum?: number;
    useSelectedLogo: boolean;
    templateIndex: number;
    templateCode: string;
    dimensions?: { width?: number; height?: number };
    images: MultiformatImage[];
    multipage: boolean;
    fontSelections: FontSelectionSummary[];
    imageUsage: ImageUsage;
    customTemplate: boolean;
    db?: string;
    fileId?: string;
    brandTones: BrandTone[];
    tone?: string;
    context?: AccountContext;
  }) =>
    debugLabel(
      `previewFamily(${imgNum})`,
      atomWithDefault(async (get) => {
        if (!imgNum) return null;
        const preview = await get(
          previewFamily({
            formatId,
            answer,
            imgNum,
            useSelectedLogo,
            templateIndex,
            templateCode,
            dimensions,
            images,
            multipage,
            fontSelections,
            imageUsage,
            db,
            fileId,
            brandTones,
            tone,
            ...(context && { context }),
          }),
        );
        if (!preview) return null;
        const url = await new Promise<string>((resolve, reject) => {
          DSHDLib.getVariableTemplatePreviewURL(preview, (err, previewUrl) => {
            if (err) {
              reject(err);
            } else {
              resolve(previewUrl);
            }
          });
        });

        return {
          preview,
          url,
          customTemplate,
          contentGenerationResultId: preview.contentGenerationResultId,
        };
      }),
    ),
  isEqual,
);

/**
 * `previewUrlFamily` is only ever read as a loadable, so the wrapper lives here
 * rather than at each call site — one derived atom per key, created once.
 */
export const loadablePreviewUrlFamily = atomFamily(
  (query: Parameters<typeof previewUrlFamily>[0]) => loadable(previewUrlFamily(query)),
  isEqual,
);
